import { AlertApiForwarder } from '@backstage/core-app-api';
import {
  AlertMessage,
  AnyApiFactory,
  alertApiRef,
  createApiFactory,
  featureFlagsApiRef,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  ExtensionDefinition,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { Confidence, visitorIdentity } from '@spotify-confidence/sdk';
import { imageUploadApiRef } from '@spotify/confidence-backstage-image-upload';
import { getSessionId } from '@spotify/confidence-core-react';
import {
  ConfidenceAuth,
  ConfidenceFeatureFlags,
  ConfidenceImageUploadClient,
  confidenceUserApiRef,
  trackErrorEvent,
} from '@spotify/confidence-core-react';
import { CreateMeasurementDialog } from '@spotify/plugin-confidence-metrics';
import { createMeasurementDialogExtensionsApiRef } from '@spotify/plugin-confidence-metrics-react';
import { TrackingConfidenceEvents } from '@spotify/plugin-confidence-tracking-module-confidence-events';
import {
  DevConsoleTracking,
  trackingApiRef,
} from '@spotify/plugin-confidence-tracking-react';

const getIdleLogger = () => {
  const idle = () => {};

  return ['info', 'warn', 'error', 'trace', 'debug'].reduce<
    Record<string, () => void>
  >(
    (acc, method) => ({
      ...acc,
      [method]: idle,
    }),
    {},
  );
};
export const confidenceApi = Confidence.create({
  clientSecret: 'NiAgIVmkjVSTNI5k8mpsgs3EeFqkacrA',
  environment: 'client',
  region: 'eu',
  timeout: 3000,
  logger: getIdleLogger(),
  context: { session_id: getSessionId() },
});

confidenceApi.track(visitorIdentity());

const apis: AnyApiFactory[] = [
  createApiFactory({
    api: catalogApiRef,
    deps: {},
    // Stub: only getEntityByRef is used by Backstage internals in this app
    factory: () =>
      ({ getEntityByRef() {} }) as unknown as typeof catalogApiRef.T,
  }),
  createApiFactory({
    api: confidenceUserApiRef,
    deps: {},
    factory: () => ConfidenceAuth.create(),
  }),
  createApiFactory({
    api: featureFlagsApiRef,
    deps: {},
    factory: () => new ConfidenceFeatureFlags(),
  }),
  createApiFactory({
    api: imageUploadApiRef,
    deps: {
      userApi: confidenceUserApiRef,
    },
    factory: ({ userApi }) => new ConfidenceImageUploadClient(userApi),
  }),
  createApiFactory({
    api: trackingApiRef,
    deps: {},
    factory: () =>
      process.env.NODE_ENV === 'development'
        ? new DevConsoleTracking()
        : new TrackingConfidenceEvents(confidenceApi),
  }),
  createApiFactory({
    api: alertApiRef,
    deps: { confidenceUserApi: confidenceUserApiRef },
    factory: ({ confidenceUserApi }) => {
      const defaultAlertApi = new AlertApiForwarder();

      return {
        alert$: () => defaultAlertApi.alert$(),
        post: (message: AlertMessage) => {
          if (message.severity === 'error') {
            trackErrorEvent(message, confidenceUserApi);
          }

          defaultAlertApi.post(message);
        },
      };
    },
  }),
  createApiFactory({
    api: createMeasurementDialogExtensionsApiRef,
    deps: {},
    factory: () => ({ component: CreateMeasurementDialog }),
  }),
];

export const AppApis: ExtensionDefinition[] = apis.map(factory =>
  ApiBlueprint.make({
    name: factory.api.id,
    params: defineParams => defineParams(factory),
  }),
);
